"use client";

import { useState, useCallback } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useTranslations } from "next-intl";
import type { Photo } from "@/content/portfolio";
import { track } from "@/lib/analytics";
import SectionHeader from "./SectionHeader";
import Reveal from "./Reveal";

const Lightbox = dynamic(() => import("./LightboxWrapper"), { ssr: false });

type Category = "all" | "street" | "portraits";

export default function PortfolioGrid({ photos }: { photos: Photo[] }) {
  const t = useTranslations("portfolioGrid");
  const [active, setActive] = useState<Category>("all");
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const tabs: { label: string; value: Category }[] = [
    { label: t("tabAll"), value: "all" },
    { label: t("tabStreet"), value: "street" },
    { label: t("tabPortraits"), value: "portraits" },
  ];

  const filtered =
    active === "all" ? photos : photos.filter((p) => p.category === active);

  const slides = filtered.map((p) => ({ src: p.src, alt: p.alt }));

  const open = useCallback((index: number) => setLightboxIndex(index), []);

  function openWithTracking(index: number, photo: Photo) {
    open(index);
    track("portfolio_image_open", {
      photo_slug: photo.slug,
      photo_title: photo.title,
      category: photo.category,
    });
  }

  return (
    <section id="portfolio" className="py-28 px-6 bg-[var(--surface)]">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <SectionHeader
            index="02"
            label={t("sectionLabel")}
            heading={t("heading")}
          />
        </Reveal>

        {/* Category tabs */}
        <div className="flex items-center gap-1 bg-[var(--bg)] rounded-full p-1 mt-12 mb-12 w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              aria-pressed={active === tab.value}
              onClick={() => {
                setActive(tab.value);
                track("portfolio_filter", { category: tab.value });
              }}
              className={`px-5 py-2.5 font-mono text-xs uppercase tracking-widest rounded-full transition-colors ${
                active === tab.value
                  ? "bg-[var(--accent)] text-[var(--on-accent)]"
                  : "text-[var(--muted)] hover:text-[var(--text)]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Masonry grid — keyed on `active` so it re-mounts and re-staggers
            (crossfade) each time the category filter changes. */}
        <Reveal
          stagger
          key={active}
          className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4"
        >
          {filtered.map((photo, i) => (
            <button
              key={photo.slug}
              onClick={() => openWithTracking(i, photo)}
              className="group block w-full overflow-hidden rounded-sm relative break-inside-avoid cursor-zoom-in"
            >
              <div className="relative w-full aspect-[3/4] overflow-hidden bg-[var(--bg)]">
                <Image
                  src={photo.src}
                  alt={photo.alt}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <span className="font-mono text-xs uppercase tracking-widest text-white">
                    {photo.title}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </Reveal>

        {filtered.length === 0 && (
          <p className="text-center text-[var(--muted)] py-20">
            {t("empty")}
          </p>
        )}
      </div>

      {lightboxIndex >= 0 && (
        <Lightbox
          open
          close={() => {
            track("portfolio_image_close", { photo_slug: filtered[lightboxIndex]?.slug });
            setLightboxIndex(-1);
          }}
          index={lightboxIndex}
          slides={slides}
        />
      )}
    </section>
  );
}
